<template>
  <FormItem
    :id="id || name"
    :label="label"
    :label-width="labelWidth"
    :required="isRequired"
    :layout="layout"
    :error-message="errorMessage"
    :help-text="helpText"
    :no-margin="noMargin || isNested"
  >
    <template #before v-if="$slots.before">
      <slot name="before"></slot>
    </template>
    
    <div class="input-wrapper" :class="{ 'is-invalid': errorMessage }">
      <div class="input-inner-wrapper">
        <slot name="prefix" v-if="$slots.prefix" class="prefix-content"></slot>
        
        <input
          :id="id || name"
          :type="['number', 'int'] ? 'text' : type"
          :value="modelValue"
          class="form-control h-[36px]"
          :class="{ 
            'is-invalid': errorMessage,
            'form-control-sm': size === 'sm',
            'form-control-lg': size === 'lg',
            'with-prefix': $slots.prefix,
            'with-suffix': $slots.suffix || (clearable && modelValue),
            ...$attrs.class
          }"
          :style="{
             width,
             ...$attrs.style,
             ...$attrs.customStyle,
          }"
          :name="name"
          :placeholder="placeholder"
          :disabled="disabled"
          :readonly="readonly"
          :maxlength="maxlength"
          :minlength="minlength"
          :autocomplete="autocomplete"
          :autofocus="autofocus"
          @input="handleInput"
          @blur="handleBlur"
          @focus="$emit('focus', $event)"
          @keyup.enter.stop="$emit('enter', $event)"
        />
        
        <div class="suffix-container" v-if="$slots.suffix || (clearable && modelValue)">
          <div v-if="clearable && modelValue" class="clear-btn" @click="clearInput">
            <SvgIcon iconName="icon-icon_universe_close_fill" class="me-1"></SvgIcon>
          </div>
          <slot name="suffix" v-if="$slots.suffix"></slot>
        </div>
      </div>
    </div>
    
    <template #after v-if="$slots.after">
      <slot name="after"></slot>
    </template>
  </FormItem>
</template>

<script setup>
import { computed, inject, onMounted, onBeforeUnmount, ref, provide } from 'vue'
import FormItem from './FormItem.vue'

const props = defineProps({
  id: {
    type: String,
    default: ''
  },
  width: {
    type: String,
    default: '',
  },
  modelValue: {
    type: [String, Number],
    default: ''
  },
  name: {
    type: String,
    required: true
  },
  type: {
    type: String,
    default: 'text'
  },
  label: {
    type: String,
    default: ''
  },
  labelWidth: {
    type: String,
    default: undefined
  },
  placeholder: {
    type: String,
    default: ''
  },
  disabled: {
    type: Boolean,
    default: false
  },
  readonly: {
    type: Boolean,
    default: false
  },
  helpText: {
    type: String,
    default: ''
  },
  maxlength: {
    type: [String, Number],
    default: undefined
  },
  minlength: {
    type: [String, Number],
    default: undefined
  },
  autocomplete: {
    type: String,
    default: 'off'
  },
  autofocus: {
    type: Boolean,
    default: false
  },
  layout: {
    type: String,
    default: undefined
  },
  size: {
    type: String,
    default: '',
    validator: (value) => ['sm', 'lg', ''].includes(value)
  },
  validateOnBlur: {
    type: Boolean,
    default: true
  },
  noMargin: {
    type: Boolean,
    default: false
  },
  clearable: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['update:modelValue', 'blur', 'focus', 'change', 'clear'])


const formContext = inject('formContext', null)

const isNested = inject('isFormItemNested', false)

provide('isFormItemNested', true)

const layout = computed(() => props.layout || (formContext ? formContext.layout : 'vertical'))
const labelWidth = computed(() => props.labelWidth || (formContext ? formContext.labelWidth : undefined))

const isRequired = computed(() => {
  if (!formContext || !formContext.rules || !formContext.rules[props.name]) return false
  
  const rules = formContext.rules[props.name]
  return rules.some(rule => rule.required)
})

const errorMessage = computed(() => {
  if (!formContext || !formContext.errors) return ''
  return formContext.errors[props.name] || ''
})

const handleInput = (event) => {
  let value = event.target.value;
  const originalEventValue = event.target.value; 

  if (props.type === 'number') {
    const initialValueForNumberProcessing = value; 
    const ALLOWED_CHARS_REGEX = /[^0-9.eE-]/g;
    value = value.replace(ALLOWED_CHARS_REGEX, '');

    let basePart = value;
    let exponentPart = null;
    const eIndex = value.toLowerCase().indexOf('e');

    if (eIndex > -1) {
      basePart = value.substring(0, eIndex);
      exponentPart = value.substring(eIndex + 1);

      if (exponentPart !== null) {
        const exponentAllowedChars = /[^0-9-]/g;
        exponentPart = exponentPart.replace(exponentAllowedChars, '');
        if (exponentPart.lastIndexOf('-') > 0) {
          exponentPart = '-' + exponentPart.replace(/-/g, '');
        } else if (exponentPart.startsWith('-') && exponentPart.match(/-/g).length > 1) {
          exponentPart = '-' + exponentPart.substring(1).replace(/-/g, '');
        }
      }
    }
    const dotParts = basePart.split('.');
    if (dotParts.length > 2) {
      basePart = dotParts[0] + '.' + dotParts.slice(1).join('');
    }

    if (basePart.includes('-')) {
        const startsWithMinusOriginal = initialValueForNumberProcessing.startsWith('-');
        if (basePart.lastIndexOf('-') > 0) { // Minus not at the start
            basePart = basePart.replace(/-/g, '');
            if (startsWithMinusOriginal) basePart = '-' + basePart;
        } else if (basePart.startsWith('-') && basePart.match(/-/g).length > 1) { // Multiple minuses at start e.g. "--5"
            basePart = '-' + basePart.substring(1).replace(/-/g, '');
        } 
    }
    
    if (exponentPart !== null) {
      value = basePart + 'e' + exponentPart;
    } else {
      value = basePart;
    }

  } else if (props.type === 'int') {
    const NON_DIGIT_REGEX = /[^0-9]/g;
    value = value.replace(NON_DIGIT_REGEX, '');
  }

  if (originalEventValue !== value) {
    event.target.value = value;
  }
  
  emit('update:modelValue', value);
}

const handleBlur = (event) => {
  emit('blur', event)
  
  if (props.validateOnBlur && formContext && formContext.validateField) {
    formContext.validateField(props.name)
  }
}

const clearInput = () => {
  emit('update:modelValue', '')
  emit('clear')
}

onMounted(() => {
  if (formContext && formContext.registerFormItem) {
    formContext.registerFormItem(props.name, {
      validate: () => formContext.validateField(props.name)
    })
  }
})

onBeforeUnmount(() => {
  if (formContext && formContext.unregisterFormItem) {
    formContext.unregisterFormItem(props.name)
  }
})
</script>

<style scoped>
.input-wrapper {
  display: flex;
  align-items: center;
  width: 100%;
  position: relative;
}

.input-inner-wrapper {
  position: relative;
  display: flex;
  align-items: center;
  width: 100%;
}

.prefix-content {
  position: absolute;
  left: 0.75rem;
  z-index: 2;
}

.form-control {
  background-color: #F5FAFF !important;
  border-color: #D8E8FF;
  font-size: 0.875rem;
  color: #343b46;
  width: 100%;
}
.form-control[disabled] {
    background-color: #ECEFF2 !important;
    border: none !important;
}

.form-control.with-prefix {
  padding-left: 2.5rem;
}

.form-control.with-suffix {
  padding-right: 1.8rem;
}

.form-control::placeholder {
  color: #8697AA;
}

.form-control.is-invalid {
  border-color: #C62741 !important;
  margin-bottom: 0 !important;
}

.form-control.is-invalid:focus {
  border-color: #C62741 !important;
  box-shadow: none !important;
}

.suffix-container {
  position: absolute;
  right: 0.25rem;
  display: inline-flex;
  flex-shrink: 0;
  text-align: center;
  align-items: center;
  justify-content: center;
  z-index: 2;
  gap: 0.5rem;
}

.clear-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  /* padding: 0.25rem; */
  /* background: none; */
  border: none;
  opacity: 0;
  transition: opacity 0.2s;
}

.input-wrapper:hover .clear-btn {
  opacity: 1;
}

.clear-btn:hover svg {
  fill: #0068B0;
}
</style> 